import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api, imgUrl, money } from "@/lib/api";
import { useSeo, useSettings } from "@/context/SettingsContext";
import { PageHero, ProductCard, FinalCta } from "@/components/site/Blocks";

export function ProductList() {
  const [items, setItems] = useState(null);
  const [type, setType] = useState("all");
  useSeo("Lawn Mower Products", "Browse gas, battery and riding lawn mowers available for a $1 home trial.");
  useEffect(() => { api.get("/public/products").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  const types = ["all", ...new Set((items || []).map((p) => p.mower_type).filter(Boolean))];
  const shown = (items || []).filter((p) => type === "all" || p.mower_type === type);
  return (
    <>
      <PageHero eyebrow="Our mowers" title="Mowers you can try at home" text="Every model below can be requested for a home trial, subject to availability in your area." />
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
        <div className="mb-10 flex flex-wrap gap-2">
          {types.map((t) => (
            <button key={t} onClick={() => setType(t)} data-testid={`product-filter-${t}`}
              className={`h-10 rounded-full px-5 text-sm font-semibold capitalize transition-colors ${type === t ? "bg-slate-900 text-white" : "border border-slate-300 text-slate-700 hover:border-emerald-600"}`}>{t}</button>
          ))}
        </div>
        {items === null ? <p className="text-slate-500">Loading mowers…</p> : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{shown.map((p) => <ProductCard key={p.id} p={p} />)}</div>
        )}
      </section>
      <FinalCta />
    </>
  );
}

export function ProductDetail() {
  const { id } = useParams();
  const { settings } = useSettings();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [err, setErr] = useState(false);
  useSeo(p?.name || "Mower", p?.description?.slice(0, 150));
  useEffect(() => { api.get(`/public/products/${id}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [id]);
  if (err) return <div className="mx-auto max-w-3xl px-4 py-24 text-center"><h1 className="font-display text-3xl font-bold">Mower not found</h1><Link to="/products" className="mt-4 inline-block text-emerald-800">Back to mowers</Link></div>;
  if (!p) return <div className="py-24 text-center text-slate-500">Loading…</div>;
  return (
    <>
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-16 lg:px-8">
        <Link to="/products" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-emerald-800"><ArrowLeft className="h-4 w-4" />All mowers</Link>
        <div className="mt-8 grid gap-12 lg:grid-cols-2">
          <div>
            <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-slate-100">
              {p.images?.[img] && <img src={imgUrl(p.images[img])} alt={p.name} className="h-full w-full object-cover" data-testid="product-detail-image" />}
            </div>
            {p.images?.length > 1 && (
              <div className="mt-4 flex gap-3">
                {p.images.map((u, i) => <button key={u} onClick={() => setImg(i)} className={`h-20 w-24 overflow-hidden rounded-lg border-2 ${i === img ? "border-emerald-600" : "border-transparent"}`}><img src={imgUrl(u)} alt="" className="h-full w-full object-cover" /></button>)}
              </div>
            )}
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-slate-500">{p.brand} · {p.model} · <span className="capitalize">{p.mower_type}</span></p>
            <h1 data-testid="product-detail-name" className="mt-2 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">{p.name}</h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">{p.description}</p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-200 p-5"><p className="text-sm text-slate-500">Retail price</p><p className="font-display text-3xl font-bold text-slate-900">{money(p.retail_price)}</p></div>
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5"><p className="text-sm text-emerald-800">Home trial fee</p><p className="font-display text-3xl font-bold text-emerald-700">{money(p.trial_fee ?? settings.trial_fee)}</p></div>
            </div>
            <p className="mt-3 text-xs text-slate-500">Trial fee collected only after your trial is confirmed. Prices exclude applicable sales tax. Availability depends on your service area.</p>
            <Link to={`/book?product=${p.id}`} data-testid="product-detail-book-button" className="mt-8 inline-flex h-14 items-center gap-2 rounded-full bg-emerald-700 px-8 font-bold text-white hover:bg-emerald-800">Request a home trial <ArrowRight className="h-5 w-5" /></Link>
            {p.specs?.length > 0 && (
              <div className="mt-10">
                <h2 className="font-display text-lg font-bold text-slate-900">Specifications</h2>
                <dl className="mt-4 divide-y divide-slate-200 rounded-2xl border border-slate-200">
                  {p.specs.map((s) => <div key={s.label} className="flex justify-between gap-4 px-5 py-3.5 text-sm"><dt className="text-slate-500">{s.label}</dt><dd className="text-right font-semibold text-slate-900">{s.value}</dd></div>)}
                </dl>
              </div>
            )}
          </div>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
